/**
 * RolePermissionPicker — the professional permission picker for role
 * creation/edit. Reads directly from the bundled `PERMISSION_CATALOG`
 * (no API calls — the catalog is already in the JS bundle).
 *
 * UX:
 *   - All 407 permissions grouped by `domain` (KYC, Disputes, Banners, ...).
 *   - Each domain is a collapsible card with select-all / clear / partial state.
 *   - Live search filters both group names and permission codes/labels.
 *   - Each row shows: Persian name, action chip, danger chip, secure/super-admin
 *     icons, dep count, snake_case code.
 *   - Toolbar: total picked, expand-all/collapse-all, "select wildcard *".
 *   - Wildcard mode (`['*']`) is rendered as a distinct full-access card —
 *     no per-permission picking when wildcard is set.
 *
 * Pure controlled component: takes `picks` + `onChange(picks)`. No internal
 * state for selection. Parent owns the picks array.
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  Stack,
  Typography,
  Chip,
  Collapse,
  Checkbox,
  IconButton,
  Tooltip,
  Button,
  Divider,
  Paper,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import StarIcon from '@mui/icons-material/Star';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import LinkIcon from '@mui/icons-material/Link';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import {
  PERMISSION_CATALOG,
  PermissionMeta,
  getAllDomains,
  getPermissionsByDomain,
  type PermissionCode,
} from '../../constants/permissionCatalog';
import { useAccess } from '../../hooks/useAccess';

interface RolePermissionPickerProps {
  picks: string[];
  onChange: (picks: string[]) => void;
  /** Show the wildcard toggle. Defaults to true for super-admin role creation. */
  allowWildcard?: boolean;
  /** Compact mode hides metadata chips, useful for narrow dialogs. */
  compact?: boolean;
}

const actionColor = (a: PermissionMeta['action']) =>
  a === 'admin' ? 'error' : a === 'write' ? 'warning' : 'default';
const dangerColor = (d: PermissionMeta['dangerLevel']) =>
  d === 'high' ? 'error' : d === 'medium' ? 'warning' : 'default';

const RolePermissionPicker: React.FC<RolePermissionPickerProps> = ({
  picks,
  onChange,
  allowWildcard = true,
  compact = false,
}) => {
  const [search, setSearch] = useState('');
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set());
  const [showOnlyPicked, setShowOnlyPicked] = useState(false);

  const isWildcard = picks.length === 1 && picks[0] === '*';
  const picksSet = useMemo(() => new Set(picks), [picks]);

  // Permissions flagged `requireSuperAdmin` can only be put on a role by a
  // super-admin — the backend rejects the whole save otherwise. Lock them in
  // the UI (instead of letting the user compose a payload that will 403).
  const { user: actor } = useAccess();
  const isLocked = (meta: PermissionMeta) => !actor.isSuperAdmin && !!meta.requireSuperAdmin;

  const domains = useMemo(() => getAllDomains(), []);
  const totalPerms = Object.keys(PERMISSION_CATALOG).length;

  /** Filter perms by search + "only picked" toggle, grouped by domain. */
  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const result: { domain: string; perms: { code: string; meta: PermissionMeta }[] }[] = [];
    for (const dom of domains) {
      const codes = getPermissionsByDomain(dom);
      const perms = codes
        .map((c) => ({ code: c as string, meta: (PERMISSION_CATALOG as Record<string, PermissionMeta>)[c] }))
        .filter(({ code, meta }) => {
          if (showOnlyPicked && !picksSet.has(code)) return false;
          if (!q) return true;
          return (
            code.toLowerCase().includes(q) ||
            (meta.nameFa && meta.nameFa.toLowerCase().includes(q)) ||
            dom.toLowerCase().includes(q)
          );
        });
      if (perms.length > 0) result.push({ domain: dom, perms });
    }
    return result;
  }, [domains, search, showOnlyPicked, picksSet]);

  /** Count picks per domain, for the header badge. */
  const picksByDomain = useMemo(() => {
    const map = new Map<string, number>();
    for (const code of picks) {
      if (code === '*') continue;
      const meta = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[code];
      if (meta) map.set(meta.domain, (map.get(meta.domain) ?? 0) + 1);
    }
    return map;
  }, [picks]);

  /** Toggle one perm. */
  const toggle = (code: string, checked: boolean) => {
    if (isWildcard) return; // wildcard owns everything
    const meta = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[code];
    if (checked && meta && isLocked(meta)) return;
    if (checked) {
      if (picksSet.has(code)) return;
      onChange([...picks, code]);
    } else {
      onChange(picks.filter((p) => p !== code));
    }
  };

  /** Select / clear all perms in a domain. */
  const setDomain = (dom: string, select: boolean) => {
    if (isWildcard) return;
    const domCodes = getPermissionsByDomain(dom) as string[];
    if (select) {
      const merged = new Set(picks);
      for (const c of domCodes) {
        const meta = (PERMISSION_CATALOG as Record<string, PermissionMeta>)[c];
        if (meta && isLocked(meta)) continue; // select-all skips super-admin-only perms
        merged.add(c);
      }
      onChange(Array.from(merged));
    } else {
      const domSet = new Set(domCodes);
      onChange(picks.filter((p) => !domSet.has(p)));
    }
  };

  const expandAll = () => setOpenGroups(new Set(filteredGroups.map((g) => g.domain)));
  const collapseAll = () => setOpenGroups(new Set());
  const toggleGroup = (dom: string) => {
    const next = new Set(openGroups);
    if (next.has(dom)) next.delete(dom);
    else next.add(dom);
    setOpenGroups(next);
  };

  // ----- Wildcard view: locks the picker, shows full-access card -----
  if (isWildcard) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderColor: 'warning.main', bgcolor: 'warning.light' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <StarIcon color="warning" fontSize="large" />
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              دسترسی کامل (Wildcard *)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              این نقش به همه دسترسی‌ها به صورت خودکار دسترسی دارد.
              هیچ پرمیشن منفردی نیاز به انتخاب نیست.
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={() => onChange([])}
          >
            حذف دسترسی کامل
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box>
      {/* ----- Toolbar ----- */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        sx={{ mb: 1.5 }}
        alignItems={{ md: 'center' }}
      >
        <TextField
          placeholder="جستجو در دسترسی‌ها (نام، کد، دامنه)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showOnlyPicked}
                onChange={(e) => setShowOnlyPicked(e.target.checked)}
              />
            }
            label="فقط انتخاب‌شده"
            sx={{ ml: 0, mr: 0 }}
          />
          <Tooltip title="باز کردن همه گروه‌ها">
            <Button size="small" startIcon={<ExpandMoreIcon />} onClick={expandAll}>
              همه
            </Button>
          </Tooltip>
          <Tooltip title="بستن همه گروه‌ها">
            <Button size="small" startIcon={<ExpandLessIcon />} onClick={collapseAll}>
              بستن
            </Button>
          </Tooltip>
          {allowWildcard && (
            <Tooltip title="دسترسی کامل (Super-Admin)">
              <Button
                size="small"
                color="warning"
                startIcon={<StarIcon />}
                onClick={() => onChange(['*'])}
              >
                دسترسی کامل
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* ----- Status bar ----- */}
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ mb: 1.5, p: 1, bgcolor: 'background.default', borderRadius: 1 }}
        alignItems="center"
        flexWrap="wrap"
      >
        <Chip
          size="small"
          icon={<VpnKeyIcon fontSize="small" />}
          label={`انتخاب شما: ${picks.length}`}
          color={picks.length > 0 ? 'primary' : 'default'}
        />
        <Chip
          size="small"
          variant="outlined"
          label={`نمایش: ${filteredGroups.reduce((s, g) => s + g.perms.length, 0)} / ${totalPerms}`}
        />
        <Chip size="small" variant="outlined" label={`${filteredGroups.length} دامنه`} />
        {picks.length > 0 && (
          <Button
            size="small"
            color="error"
            startIcon={<ClearAllIcon />}
            onClick={() => onChange([])}
          >
            حذف همه انتخاب‌ها
          </Button>
        )}
      </Stack>

      {/* ----- Empty state ----- */}
      {filteredGroups.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
          هیچ دسترسی با این فیلتر یافت نشد.
        </Box>
      )}

      {/* ----- Domain groups ----- */}
      <Stack spacing={1.5}>
        {filteredGroups.map(({ domain, perms }) => {
          const totalInDomain = (getPermissionsByDomain(domain) as string[]).length;
          const pickedInDomain = picksByDomain.get(domain) ?? 0;
          const isOpen = openGroups.has(domain) || search.trim().length > 0;
          const allSelected = pickedInDomain >= totalInDomain && totalInDomain > 0;
          const someSelected = pickedInDomain > 0 && !allSelected;

          return (
            <Paper key={domain} variant="outlined">
              {/* Header */}
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 2,
                  py: 1,
                  cursor: 'pointer',
                  borderBottom: isOpen ? 1 : 0,
                  borderColor: 'divider',
                  bgcolor: pickedInDomain > 0 ? 'primary.lighter' : 'background.paper',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
                onClick={() => toggleGroup(domain)}
              >
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={someSelected}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setDomain(domain, e.target.checked)}
                  inputProps={{ 'aria-label': `select all ${domain}` }}
                />
                <Typography variant="subtitle2" fontWeight={700} flex={1}>
                  {domain}
                </Typography>
                <Chip
                  size="small"
                  variant={pickedInDomain > 0 ? 'filled' : 'outlined'}
                  color={pickedInDomain > 0 ? 'primary' : 'default'}
                  label={`${pickedInDomain} / ${totalInDomain}`}
                />
                <IconButton size="small">
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Stack>

              {/* Body */}
              <Collapse in={isOpen}>
                <Box sx={{ p: 1.5 }}>
                  <Stack
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        lg: compact ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                      },
                      gap: 1,
                    }}
                  >
                    {perms.map(({ code, meta }) => {
                      const checked = picksSet.has(code);
                      const locked = isLocked(meta);
                      return (
                        <Box
                          key={code}
                          title={locked ? 'فقط سوپرادمین می‌تواند این دسترسی را به نقش اضافه کند' : undefined}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.75,
                            p: 1,
                            borderRadius: 1,
                            opacity: locked ? 0.55 : 1,
                            border: 1,
                            borderColor: checked ? 'primary.main' : 'divider',
                            bgcolor: checked ? 'primary.lighter' : 'transparent',
                            cursor: 'pointer',
                            transition: 'border-color 0.15s, background 0.15s',
                            '&:hover': { borderColor: checked ? 'primary.dark' : 'text.disabled' },
                          }}
                          onClick={() => { if (!locked) toggle(code, !checked); }}
                        >
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={locked}
                            onChange={(e) => toggle(code, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ p: 0, mt: 0.25 }}
                          />
                          <Box flex={1} minWidth={0}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                              {meta.nameFa}
                            </Typography>
                            <Typography
                              variant="caption"
                              component="div"
                              sx={{
                                color: 'text.secondary',
                                fontFamily: 'monospace',
                                fontSize: '0.7rem',
                                direction: 'ltr',
                                textAlign: 'left',
                              }}
                            >
                              {code}
                            </Typography>
                            {!compact && (
                              <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                                <Chip
                                  size="small"
                                  label={meta.action}
                                  color={actionColor(meta.action) as any}
                                  sx={{ height: 16, fontSize: '0.65rem' }}
                                />
                                <Chip
                                  size="small"
                                  label={meta.dangerLevel}
                                  color={dangerColor(meta.dangerLevel) as any}
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.65rem' }}
                                />
                                {meta.secure && (
                                  <Tooltip title="حساس — مشاهده خارج از شیفت ممنوع">
                                    <LockIcon sx={{ fontSize: 14, color: 'error.main' }} />
                                  </Tooltip>
                                )}
                                {meta.requireSuperAdmin && (
                                  <Tooltip title="فقط سوپر-ادمین">
                                    <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                  </Tooltip>
                                )}
                                {meta.dependencies && meta.dependencies.length > 0 && (
                                  <Tooltip
                                    title={
                                      'به صورت خودکار اضافه می‌کند: ' + meta.dependencies.join('، ')
                                    }
                                  >
                                    <Chip
                                      size="small"
                                      icon={<LinkIcon sx={{ fontSize: 12 }} />}
                                      label={`+${meta.dependencies.length}`}
                                      variant="outlined"
                                      sx={{ height: 16, fontSize: '0.65rem' }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                  {!showOnlyPicked && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button
                          size="small"
                          startIcon={<DoneAllIcon />}
                          onClick={() => setDomain(domain, true)}
                          disabled={allSelected}
                        >
                          انتخاب همه این دامنه
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<ClearAllIcon />}
                          onClick={() => setDomain(domain, false)}
                          disabled={pickedInDomain === 0}
                        >
                          پاک کردن این دامنه
                        </Button>
                      </Stack>
                    </>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default RolePermissionPicker;
